import { useNavigate, Link } from 'react-router-dom'
import { FlightPayload, PilotPayload, countryFlag } from './types'
import { Table, TableBody, TableCell, TableRow } from '../components/table'
import { useAuthStatus } from './user_dropdown'
import { PilotAvatar } from './pilot_avatar'
import {
  ArrowTrendingUpIcon,
  ChatBubbleLeftEllipsisIcon,
  MapPinIcon,
  PhotoIcon,
  PlusCircleIcon,
  UsersIcon,
} from '@heroicons/react/20/solid'
import { FlightTypeIconMeta, getFlightTypeIconMeta } from './flight_type_icon'
import { buildFlightRowViewModel } from './flight_list.helpers'

function FlightTypeIcon({ meta }: { meta: FlightTypeIconMeta }) {
  if (meta.icon === 'hike') return <ArrowTrendingUpIcon className="h-4 w-4" />
  if (meta.icon === 'tandem') return <UsersIcon className="h-4 w-4" />
  return <MapPinIcon className="h-4 w-4" />
}

interface FlightListProps {
  flights: FlightPayload[]
  showAvatar?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  onAddPilotFilter?: (nick: string) => void
  onAddTakeoffFilter?: (id: number, name: string) => void
}

function FlightRow({ flight, pilot, onAddPilotFilter, onAddTakeoffFilter }: {
  flight: FlightPayload
  pilot?: PilotPayload | { name?: string; nick?: string; avatar?: string }
  onAddPilotFilter?: (nick: string) => void
  onAddTakeoffFilter?: (id: number, name: string) => void
}) {
  const navigate = useNavigate()

  // Prefer per-flight pilot info (from feed) over the passed-in prop.
  const displayPilot = flight.pilot_nick
    ? { name: flight.pilot_name, nick: flight.pilot_nick, avatar: flight.pilot_avatar, profile_picture_id: flight.pilot_profile_picture_id }
    : pilot
  const flightTypeMeta = getFlightTypeIconMeta(flight.type)
  const rowView = buildFlightRowViewModel(flight, displayPilot !== undefined)

  return (
    <TableRow
      className="hover:bg-[#BFC0C0]/30 dark:hover:bg-[#4F5D75]/30 transition-colors cursor-pointer"
      onClick={() => navigate(`/flight/${flight.id}`)}
    >
      <TableCell colSpan={rowView.mobileColSpan} className="sm:hidden whitespace-normal py-3">
        <div className="flex flex-col gap-2.5">
          <div className="min-w-0 font-medium text-[#2D3142] dark:text-white">
            <Link
              to={`/flight/${flight.id}`}
              onClick={e => e.stopPropagation()}
              className="line-clamp-2 hover:text-[#EF8354] transition-colors"
            >
              {rowView.title}
            </Link>
          </div>
          <div className="flex items-start gap-2.5">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EF8354]/12 text-[#EF8354] dark:bg-[#EF8354]/18"
                title={flightTypeMeta.label}
                aria-label={flightTypeMeta.label}
              >
                <FlightTypeIcon meta={flightTypeMeta} />
              </div>
              <div className="min-w-0 flex-1 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
                <span className="group/takeoff inline-flex items-center gap-1 min-w-0">
                  {rowView.takeoffCountry && (
                    <span className="shrink-0 text-base leading-none" title={rowView.takeoffCountry}>{countryFlag(rowView.takeoffCountry)}</span>
                  )}
                  {rowView.takeoffHref ? (
                    <Link
                      to={rowView.takeoffHref}
                      onClick={e => e.stopPropagation()}
                      className="truncate text-[#EF8354] hover:underline"
                    >
                      {rowView.takeoffLabel}
                    </Link>
                  ) : (
                    <span>{rowView.takeoffLabel}</span>
                  )}
                  {onAddTakeoffFilter && flight.takeoff_id != null && (
                    <button
                      onClick={e => { e.stopPropagation(); onAddTakeoffFilter(flight.takeoff_id!, flight.takeoff_name) }}
                      title={`Filter by ${flight.takeoff_name}`}
                      className="shrink-0 opacity-0 group-hover/takeoff:opacity-100 transition-opacity"
                    >
                      <PlusCircleIcon className="h-3.5 w-3.5 text-[#EF8354]" />
                    </button>
                  )}
                </span>
                {rowView.gliderName && (
                  <span className="block truncate text-xs text-[#4F5D75]/70 dark:text-[#BFC0C0]/70">{rowView.gliderName}</span>
                )}
              </div>
            </div>
            {displayPilot !== undefined && (
              'nick' in displayPilot && displayPilot.nick ? (
                <div className="group/pilot relative shrink-0 self-start">
                  <Link
                    to={`/pilot/${displayPilot.nick}`}
                    onClick={e => e.stopPropagation()}
                    title={displayPilot.name ?? displayPilot.nick}
                  >
                    <PilotAvatar
                      pilot={displayPilot}
                      className="size-8 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
                    />
                  </Link>
                  {onAddPilotFilter && (
                    <button
                      onClick={e => { e.stopPropagation(); onAddPilotFilter(displayPilot.nick!) }}
                      title={`Filter by ${displayPilot.nick}`}
                      className="absolute -right-1 -top-1 rounded-full bg-white dark:bg-[#2D3142] opacity-0 group-hover/pilot:opacity-100 transition-opacity"
                    >
                      <PlusCircleIcon className="h-4 w-4 text-[#EF8354]" />
                    </button>
                  )}
                </div>
              ) : (
                <PilotAvatar
                  pilot={displayPilot}
                  className="size-8 shrink-0 self-start bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
                />
              )
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
            <span>{rowView.dateLabel}</span>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span>{rowView.distanceLabel}</span>
              <span>{rowView.durationLabel}</span>
              {rowView.photoTitle && (
                <PhotoIcon
                  className="h-4 w-4 text-[#4F5D75]/60 dark:text-[#BFC0C0]/60"
                  title={rowView.photoTitle}
                />
              )}
              {rowView.commentTitle && (
                <ChatBubbleLeftEllipsisIcon
                  className="h-4 w-4 text-[#4F5D75]/60 dark:text-[#BFC0C0]/60"
                  title={rowView.commentTitle}
                />
              )}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden w-10 pr-0 sm:table-cell">
        <div
          className="flex size-8 items-center justify-center rounded-full bg-[#EF8354]/12 text-[#EF8354] dark:bg-[#EF8354]/18"
          title={flightTypeMeta.label}
          aria-label={flightTypeMeta.label}
        >
          <FlightTypeIcon meta={flightTypeMeta} />
        </div>
      </TableCell>
      {displayPilot !== undefined && (
        <TableCell className="hidden w-10 pr-0 sm:table-cell">
          {'nick' in displayPilot && displayPilot.nick ? (
            <div className="group/pilot relative inline-flex">
              <Link
                to={`/pilot/${displayPilot.nick}`}
                onClick={e => e.stopPropagation()}
                title={displayPilot.name ?? displayPilot.nick}
              >
                <PilotAvatar
                  pilot={displayPilot}
                  className="size-8 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
                />
              </Link>
              {onAddPilotFilter && (
                <button
                  onClick={e => { e.stopPropagation(); onAddPilotFilter(displayPilot.nick!) }}
                  title={`Filter by ${displayPilot.nick}`}
                  className="absolute -right-1 -top-1 rounded-full bg-white dark:bg-[#2D3142] opacity-0 group-hover/pilot:opacity-100 transition-opacity"
                >
                  <PlusCircleIcon className="h-4 w-4 text-[#EF8354]" />
                </button>
              )}
            </div>
          ) : (
            <PilotAvatar
              pilot={displayPilot}
              className="size-8 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
            />
          )}
        </TableCell>
      )}
      <TableCell className="hidden text-[#4F5D75] dark:text-[#BFC0C0] sm:table-cell">
        {rowView.dateLabel}
      </TableCell>
      <TableCell className="hidden font-medium text-[#2D3142] dark:text-white sm:table-cell">
        <Link
          to={`/flight/${flight.id}`}
          onClick={e => e.stopPropagation()}
          className="hover:text-[#EF8354] transition-colors"
        >
          {rowView.title}
        </Link>
      </TableCell>
      <TableCell className="hidden text-[#4F5D75] dark:text-[#BFC0C0] sm:table-cell">
        <div>
          <span className="group/takeoff inline-flex items-center gap-1">
            {rowView.takeoffCountry && (
              <span className="shrink-0 text-base leading-none" title={rowView.takeoffCountry}>{countryFlag(rowView.takeoffCountry)}</span>
            )}
            {rowView.takeoffHref ? (
              <Link
                to={rowView.takeoffHref}
                onClick={e => e.stopPropagation()}
                className="text-[#EF8354] hover:underline"
              >
                {rowView.takeoffLabel}
              </Link>
            ) : rowView.takeoffLabel}
            {onAddTakeoffFilter && flight.takeoff_id != null && (
              <button
                onClick={e => { e.stopPropagation(); onAddTakeoffFilter(flight.takeoff_id!, flight.takeoff_name) }}
                title={`Filter by ${flight.takeoff_name}`}
                className="opacity-0 group-hover/takeoff:opacity-100 transition-opacity"
              >
                <PlusCircleIcon className="h-3.5 w-3.5 text-[#EF8354]" />
              </button>
            )}
          </span>
          {rowView.gliderName && (
            <span className="block truncate text-xs text-[#4F5D75]/70 dark:text-[#BFC0C0]/70">{rowView.gliderName}</span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden text-right text-[#4F5D75] dark:text-[#BFC0C0] sm:table-cell">
        {rowView.distanceLabel}
      </TableCell>
      <TableCell className="hidden text-right text-[#4F5D75] dark:text-[#BFC0C0] sm:table-cell">
        {rowView.durationLabel}
      </TableCell>
      <TableCell className="hidden w-16 pl-0 sm:table-cell">
        <div className="flex items-center gap-1.5">
          {rowView.photoTitle && (
            <PhotoIcon
              className="h-4 w-4 text-[#4F5D75]/60 dark:text-[#BFC0C0]/60"
              title={rowView.photoTitle}
            />
          )}
          {rowView.commentTitle && (
            <ChatBubbleLeftEllipsisIcon
              className="h-4 w-4 text-[#4F5D75]/60 dark:text-[#BFC0C0]/60"
              title={rowView.commentTitle}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

export function FlightList({ flights, showAvatar = true, hasMore, loadingMore, onLoadMore, onAddPilotFilter, onAddTakeoffFilter }: FlightListProps) {
  const { userData } = useAuthStatus()
  const pilot = showAvatar ? userData.pilot : undefined

  return (
    <div className="overflow-hidden shadow-lg outline-1 outline-[#BFC0C0]/50 rounded-2xl bg-gradient-to-br from-white to-[#BFC0C0]/20 dark:from-[#2D3142] dark:to-[#4F5D75] dark:shadow-[#2D3142]/20 dark:outline-[#4F5D75]/30">
      <Table className="relative min-w-full divide-y divide-[#BFC0C0] dark:divide-[#4F5D75]">
        <TableBody className="bg-white/80 dark:bg-[#2D3142]/30">
          {flights.map(flight => (
            <FlightRow
              key={flight.id}
              flight={flight}
              pilot={pilot}
              onAddPilotFilter={onAddPilotFilter}
              onAddTakeoffFilter={onAddTakeoffFilter}
            />
          ))}
        </TableBody>
      </Table>
      {hasMore && onLoadMore && (
        <div className="flex justify-center py-4 bg-white/80 dark:bg-[#2D3142]/30 border-t border-[#BFC0C0]/30 dark:border-[#4F5D75]/30">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-[#EF8354] hover:text-[#EF8354]/80 disabled:opacity-50 transition-colors"
          >
            {loadingMore ? 'Loading…' : 'Load more flights'}
          </button>
        </div>
      )}
    </div>
  )
}
